import { stateNameToAbbr, US_STATE_NAMES, usStateFullName } from '@/utils/publicForms.js'

export function courseSportName(store) {
  return String(
    store?.selected_course?.sport
    || store?.user_course?.sport
    || store?.currentCourse?.sport
    || ''
  ).trim()
}

export function publicationSportLabel(publication) {
  return String(publication?.sportLabel || publication?.sport || '').trim() || 'General'
}

function compareText(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  })
}

export function publicationMatchesSport(publication, sport) {
  const query = String(sport || '').trim().toLowerCase()
  if (!query) return true
  const fields = [
    publication?.sport,
    publication?.sportLabel,
    publication?.title,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
  if (!fields.length) return false
  return fields.some((field) => field.includes(query) || query.includes(field))
}

export function filterPublicationsBySport(publications, sport) {
  const list = Array.isArray(publications) ? publications : []
  const query = String(sport || '').trim()
  if (!query) return list
  return list.filter((publication) => publicationMatchesSport(publication, query))
}

export function isCurrentPublication(publication) {
  if (!publication) return false
  const lifecycle = String(publication.lifecycle || '').trim().toLowerCase()
  if (lifecycle === 'previous' || lifecycle === 'future') return false
  if (publication.status === 'FUTURE_ASSIGNED') return false
  return true
}

export function filterCurrentPublications(publications) {
  const list = Array.isArray(publications) ? publications : []
  return list.filter(isCurrentPublication)
}

/** Previous editions are hidden from org browse/assign; current and future remain visible. */
export function isPreviousPublication(publication) {
  if (!publication) return false
  return String(publication.lifecycle || '').trim().toLowerCase() === 'previous'
}

export function filterOutPreviousPublications(publications) {
  const list = Array.isArray(publications) ? publications : []
  return list.filter((publication) => !isPreviousPublication(publication))
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizePublicationType(value) {
  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

/** State-specific NFHS titles (e.g. "Missouri Basketball Rules Book"). */
export function isStatePublication(publication) {
  const type = normalizePublicationType(
    publication?.type ?? publication?.bookType ?? publication?.publicationType
  )
  return type === 'state publication' || type === 'state publications'
}

function titleContainsPhrase(title, phrase) {
  const needle = String(phrase || '').trim()
  if (!needle) return false
  const re = new RegExp(`(?:^|[^a-z])${escapeRegExp(needle)}(?:[^a-z]|$)`, 'i')
  return re.test(String(title || ''))
}

/** Match 2-letter codes only when they appear as an uppercase token, so "IN" ≠ "in". */
function titleContainsStateAbbr(title, abbr) {
  const needle = String(abbr || '').trim().toUpperCase()
  if (!needle) return false
  const re = new RegExp(`(?:^|[^A-Za-z])${escapeRegExp(needle)}(?:[^A-Za-z]|$)`)
  return re.test(String(title || ''))
}

function resolveOrganizationState(orgState) {
  if (orgState && typeof orgState === 'object') {
    return resolveOrganizationState(
      orgState.state || orgState.orgState || orgState.abbreviation
    )
  }
  const name = usStateFullName(orgState)
  const abbr = name
    ? stateNameToAbbr(name)
    : String(orgState || '').trim().toUpperCase()
  return { name, abbr }
}

/**
 * State Publications stay hidden unless the org's state appears in the title.
 * "MO" matches "Missouri Basketball Rules Book"; other states do not.
 */
export function publicationVisibleForOrganizationState(publication, orgState) {
  if (!isStatePublication(publication)) return true
  const { name, abbr } = resolveOrganizationState(orgState)
  if (!name && !abbr) return false

  const title = String(publication?.title || '')
  const orgMatched = (name && titleContainsPhrase(title, name))
    || (abbr && titleContainsStateAbbr(title, abbr))
  if (!orgMatched) return false

  // Prefer the longest state name in the title ("West Virginia" over "Virginia").
  return !US_STATE_NAMES.some((otherName) => {
    if (!name || otherName.toLowerCase() === name.toLowerCase()) return false
    if (otherName.length <= name.length) return false
    return titleContainsPhrase(title, otherName)
  })
}

export function filterPublicationsForOrganizationState(publications, orgState) {
  const list = Array.isArray(publications) ? publications : []
  return list.filter((publication) => publicationVisibleForOrganizationState(publication, orgState))
}

export function normalizeSelectedSports(sports) {
  const seen = new Set()
  const next = []
  for (const sport of Array.isArray(sports) ? sports : []) {
    const label = String(sport || '').trim()
    const key = label.toLowerCase()
    if (!label || seen.has(key)) continue
    seen.add(key)
    next.push(label)
  }
  return next
}

export function uniquePublicationSports(publications) {
  return normalizeSelectedSports(
    (Array.isArray(publications) ? publications : []).map((publication) => publicationSportLabel(publication))
  ).sort(compareText)
}

export function publicationMatchesSports(publication, sports) {
  const selected = normalizeSelectedSports(sports).map((sport) => sport.toLowerCase())
  if (!selected.length) return true
  const label = publicationSportLabel(publication).toLowerCase()
  const sport = String(publication?.sport || '').trim().toLowerCase()
  return selected.some((query) => query === label || query === sport)
}

export function filterPublicationsBySports(publications, sports) {
  const list = Array.isArray(publications) ? publications : []
  const selected = normalizeSelectedSports(sports)
  if (!selected.length) return list
  return list.filter((publication) => publicationMatchesSports(publication, selected))
}

export function sortPublicationsBySportThenTitle(publications) {
  return [...(Array.isArray(publications) ? publications : [])].sort((left, right) => {
    const sportCompare = compareText(publicationSportLabel(left), publicationSportLabel(right))
    if (sportCompare !== 0) return sportCompare
    return compareText(left?.title, right?.title)
  })
}

export function resolveInitialPublicationSports(publications, sport) {
  const query = String(sport || '').trim()
  if (!query) return []
  const matched = uniquePublicationSports(publications).filter((label) => {
    const value = label.toLowerCase()
    const needle = query.toLowerCase()
    return value === needle || value.includes(needle) || needle.includes(value)
  })
  return matched.length ? matched : [query]
}
